import React, { useState } from 'react'
import {
  Container,
  Eyebrow,
  SectionTitle,
  Lede,
  Card,
  IconBadge,
} from '../AumhumSite/sections'
import {
  ArrowRight,
  Baby,
  Brain,
  ChevronDown,
  HeartPulse,
  Puzzle,
  ShieldCheck,
  Stethoscope,
} from '../AumhumSite/icons'
import { PATHWAYS, resolvePathway } from './constants'
import StepDots from './StepDots'

const ICONS = {
  Brain,
  HeartPulse,
  ShieldCheck,
  Baby,
  Puzzle,
  Stethoscope,
}

// Step 1 of the prospect signup. The user picks the kind of care they need.
//
// UX shape:
//  - Tiles with NO sub-options commit immediately on click — the click
//    *is* the action. There is no separate "Continue" button; the older
//    flow's two-step "select then continue" was confusing because the
//    Continue button sat below the fold on mobile and looked detached
//    from the cards.
//  - The one tile with sub-options ("Mental health") expands inline as
//    an accordion. Clicking the sub-option commits. Clicking the parent
//    again collapses it.
//  - Once a commit is in flight, every tile is disabled and the clicked
//    tile shows a "Continuing…" hint so a slow network can't double-fire
//    the orchestrator.
//
// The picker hands the resolved selection to the orchestrator via
// `onPick({ topId, subId, pathway, specialtyTag, topLabel, subLabel })`.
// The orchestrator decides what happens next (OTP step for anonymous
// visitors, direct /initialize for already-signed-in users).
export default function PathwayPicker({ initialTopId = null, initialSubId = null, onPick }) {
  // `expandedId` only matters for tiles that have subOptions (i.e.
  // "Mental health"). Other tiles never sit in an "expanded" state —
  // they commit on click.
  const [expandedId, setExpandedId] = useState(initialTopId)
  const [committingId, setCommittingId] = useState(null)

  const commit = async (topId, subId) => {
    if (committingId) return
    const resolved = resolvePathway(topId, subId)
    if (!resolved || !onPick) return
    setCommittingId(subId || topId)
    try {
      await onPick({ topId, subId, ...resolved })
    } finally {
      // If onPick navigated away (window.location.assign / hard nav) the
      // component unmounts before this runs, which is fine. If onPick
      // failed and stayed on the page, we re-enable the tiles so the
      // user can retry.
      setCommittingId(null)
    }
  }

  const handleTopClick = (p) => {
    if (committingId) return
    if (p.subOptions && p.subOptions.length > 0) {
      // Accordion: clicking the open tile collapses it; clicking any
      // other expandable tile opens it.
      setExpandedId((cur) => (cur === p.id ? null : p.id))
      return
    }
    // Leaf tile — click is the action.
    commit(p.id, null)
  }

  return (
    <Container className="pps-shell">
      <StepDots step={1} of={3} />
      <Eyebrow>Get matched</Eyebrow>
      <SectionTitle as="h1">What kind of care are you looking for?</SectionTitle>
      <Lede>
        Tap whatever fits best — that&rsquo;s it. We&rsquo;ll find a vetted specialist
        and you can refine details after you create your account.
      </Lede>

      <div className="pps-pathway-grid">
        {PATHWAYS.map((p) => {
          const Icon = ICONS[p.iconName] || Stethoscope
          const isExpandable = !!(p.subOptions && p.subOptions.length > 0)
          const isExpanded = isExpandable && expandedId === p.id
          const isCommitting = committingId === p.id // leaf-tile commits use top id
          const anyCommitting = !!committingId
          return (
            <Card
              key={p.id}
              className={`pps-pathway-card${isExpanded ? ' is-active' : ''}${isCommitting ? ' is-committing' : ''}`}
              emphasised={isExpanded}
            >
              <button
                type="button"
                className="pps-pathway-button"
                aria-pressed={isExpanded}
                aria-expanded={isExpandable ? isExpanded : undefined}
                aria-busy={isCommitting || undefined}
                disabled={anyCommitting}
                onClick={() => handleTopClick(p)}
              >
                <span className="pps-pathway-icon">
                  <IconBadge Icon={Icon} variant={isExpanded ? 'green' : 'violet'} />
                </span>
                <span className="pps-pathway-text">
                  <span className="pps-pathway-label">{p.label}</span>
                  <span className="pps-pathway-blurb">{p.blurb}</span>
                </span>
                {isExpandable ? (
                  <span className={`pps-pathway-chev${isExpanded ? ' is-rotated' : ''}`} aria-hidden>
                    <ChevronDown />
                  </span>
                ) : (
                  <span className="pps-pathway-chev pps-pathway-chev--leaf" aria-hidden>
                    {isCommitting ? (
                      <span className="pps-spinner" aria-hidden />
                    ) : (
                      <ArrowRight />
                    )}
                  </span>
                )}
              </button>

              {isExpanded ? (
                <div className="pps-suboptions" role="group" aria-label={`${p.label} options`}>
                  {p.subOptions.map((s) => {
                    const subCommitting = committingId === s.id
                    return (
                      <button
                        key={s.id}
                        type="button"
                        className={`pps-subopt${subCommitting ? ' is-committing' : ''}`}
                        disabled={anyCommitting}
                        aria-busy={subCommitting || undefined}
                        onClick={() => commit(p.id, s.id)}
                      >
                        <span className="pps-subopt-text">
                          <span className="pps-subopt-label">{s.label}</span>
                          <span className="pps-subopt-blurb">{s.blurb}</span>
                        </span>
                        <span className="pps-subopt-cta" aria-hidden>
                          {subCommitting ? (
                            <span className="pps-spinner" aria-hidden />
                          ) : (
                            <ArrowRight />
                          )}
                        </span>
                      </button>
                    )
                  })}
                </div>
              ) : null}
            </Card>
          )
        })}
      </div>

      {/* Subtle helper line replaces the old Continue button — confirms
          there's nothing else to click and quietly tells the user what
          happens next. */}
      <p className="pps-pathway-helper" role="status">
        {committingId
          ? 'Continuing…'
          : 'Tap any option above to continue. We’ll set up your care request right after.'}
      </p>
      {/* Hidden initialSubId restoration: when the orchestrator resumes a
          previously-stored draft, its initialSubId only matters if the
          parent is also expanded. The expanded state already comes from
          initialTopId; the sub-pick is committed live, not pre-selected. */}
      <span hidden aria-hidden data-initial-sub-id={initialSubId || ''} />
    </Container>
  )
}
